import {
  buildPath,
  buildTemplatePath,
  Card,
  GrabIconButton,
  Grid,
  ListContext,
  newToast,
  Page,
  
  PageRoute,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@compass/core";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CustomDataModelField, CustomDataModelFieldListResource, Permission } from "generated/openApi";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { customDataModelFieldPageRoute } from "../CustomDataModelFieldPage/CustomDataModelFieldPage";

const routeName = "customDataModelFields";

export const customDataModelFieldListPageRoute: PageRoute = {
  handle: "Custom Fields",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const CustomDataModelFieldListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;

  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [resource, setResource] = useState<CustomDataModelFieldListResource>({});
  const [selectedCustomDataModelTypeId, setSelectedCustomDataModelTypeId] = useState<number>(1);
  const [customDataModelFields, setCustomDataModelFields] = useState<CustomDataModelField[]>();

  useEffect(() => {
    fetchResource();
  }, []);

  useEffect(() => {
    if (selectedCustomDataModelTypeId !== undefined && selectedCustomDataModelTypeId > 0) {
      getList(selectedCustomDataModelTypeId);
    }
  }, [selectedCustomDataModelTypeId]);

  const getList = (customDataModelTypeId: number) => {
    ApiService.customDataModelFields.getList(customDataModelTypeId).then((response) => {
      setCustomDataModelFields(response);
    });
  };

  const fetchResource = () => {
    ApiService.customDataModelFields.getListResource().then((response) => {
      setResource(response);
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const reorderedItems = Array.from(customDataModelFields!);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);

    setCustomDataModelFields(reorderedItems);

    ApiService.customDataModelFields
      .assignDisplayOrders(
        selectedCustomDataModelTypeId,
        reorderedItems?.map((x) => x.id)
      )
      .then(() => {
        getList(selectedCustomDataModelTypeId);
        dispatch(setAppToast(newToast("success")));
      });
  };

  return (
    <>
      <Page header={customDataModelFieldListPageRoute.handle}>
        <Stack spacing="md">
          <ToggleButtonGroup value={selectedCustomDataModelTypeId} exclusive>
            {resource.customDataModelTypes?.map((type) => (
              <ToggleButton
                key={type.id}
                value={type.id}
                onClick={(e: React.MouseEvent<HTMLElement>, value: number) => {
                  setSelectedCustomDataModelTypeId(value);
                }}
              >
                {type.name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={"customDataModelDroppableId"}>
              {(provided) => (
                <Grid container spacing="sm" ref={provided.innerRef} {...provided.droppableProps}>
                  {customDataModelFields?.map((field, index) => (
                    <Draggable key={field.id} draggableId={field.id.toString()} index={index}>
                      {(provided) => (
                        <Grid item xs={12} key={field.id} ref={provided.innerRef} {...provided.draggableProps}>
                          <Card
                            //fullHeight
                            onClick={() =>
                              navigate(customDataModelFieldPageRoute.getPath({ id: field.id, crudMode: "view" }))
                            }
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Stack
                                direction="row"
                                spacing={"sm"}
                                color={field.isActive ? theme.palette.success.main : undefined}
                                alignItems="center"
                              >
                                <Stack direction="row" spacing="md" alignItems="center">
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {field?.name}
                                  </Typography>

                                  {field.isActive && <CheckCircleIcon color="success" />}
                                </Stack>

                                <Typography variant="caption">
                                  {field?.fieldType?.name} {field.isMandatory && <>*</>}
                                </Typography>
                              </Stack>
                              <GrabIconButton {...provided.dragHandleProps} />
                            </Stack>
                          </Card>
                        </Grid>
                      )}
                    </Draggable>
                  ))}
                </Grid>
              )}
            </Droppable>
          </DragDropContext>
        </Stack>
      </Page>

      <ListContext.Provider value={{ loadList: () => getList(selectedCustomDataModelTypeId) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
